import type {
  CreateDeliverable,
  Deliverable,
  Epic,
  PatchDeliverable,
  PatchEpic,
  PatchGoal,
  PersonRef,
  ProductGoal,
  RoadmapSnapshot,
} from '@mona-roadmap/domain';
import { deriveProductArea, deriveType } from './derive';

/**
 * Pure helpers that update the cached roadmap snapshot optimistically and roll back a failed
 * save field by field (so concurrent edits to other fields/items are never clobbered).
 */

export type DeliverableChanges = Omit<PatchDeliverable, 'expected'>;
export type GoalChanges = Omit<PatchGoal, 'expected'>;
export type EpicChanges = Omit<PatchEpic, 'expected'>;

function resolvePeople(ids: string[], people: PersonRef[]): PersonRef[] {
  const byId = new Map(people.map((p) => [p.id, p]));
  return ids.map((id) => byId.get(id) ?? { id, name: id, kind: 'person' as const });
}

const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

function epicMap(s: RoadmapSnapshot): Map<string, Epic> {
  return new Map(s.epics.map((e) => [e.id, e]));
}

function restoreKeys<T extends object>(cur: T, keys: Array<keyof T>, before: T, optimistic: T): T {
  const next = { ...cur };
  for (const k of keys) {
    if (same(cur[k], optimistic[k])) next[k] = before[k];
  }
  return next;
}

// ---- deliverables ------------------------------------------------------------------------

/** Domain fields of a Deliverable touched by the given request fields. */
export function deliverableKeysTouched(changes: DeliverableChanges): Array<keyof Deliverable> {
  const keys: Array<keyof Deliverable> = [];
  if ('name' in changes) keys.push('name');
  if ('productGoalId' in changes) keys.push('productGoalIds');
  if ('epicId' in changes) keys.push('epicIds', 'productArea', 'type');
  if ('pmOwnerIds' in changes) keys.push('pmOwners');
  if ('assigneeIds' in changes) keys.push('assignees');
  if ('sprint' in changes) keys.push('sprint');
  if ('scopingStatus' in changes) keys.push('scopingStatus');
  if ('scopingDue' in changes) keys.push('scopingDue');
  if ('status' in changes) keys.push('status');
  return keys;
}

export function applyDeliverableChanges(
  d: Deliverable,
  changes: DeliverableChanges,
  people: PersonRef[],
  epicsById: ReadonlyMap<string, Epic>,
): Deliverable {
  const next: Deliverable = { ...d };
  if (changes.name !== undefined) next.name = changes.name;
  if (changes.productGoalId !== undefined) next.productGoalIds = changes.productGoalId ? [changes.productGoalId] : [];
  if (changes.epicId !== undefined) {
    next.epicIds = changes.epicId ? [changes.epicId] : [];
    next.productArea = deriveProductArea(next.epicIds, epicsById);
    next.type = deriveType(next.epicIds, epicsById);
  }
  if (changes.pmOwnerIds !== undefined) next.pmOwners = resolvePeople(changes.pmOwnerIds, people);
  if (changes.assigneeIds !== undefined) next.assignees = resolvePeople(changes.assigneeIds, people);
  if (changes.sprint !== undefined) next.sprint = changes.sprint;
  if (changes.scopingStatus !== undefined) next.scopingStatus = changes.scopingStatus;
  if (changes.scopingDue !== undefined) next.scopingDue = changes.scopingDue;
  if (changes.status !== undefined) next.status = changes.status;
  return next;
}

function mapDeliverable(s: RoadmapSnapshot, id: string, fn: (d: Deliverable) => Deliverable): RoadmapSnapshot {
  let hit = false;
  const deliverables = s.deliverables.map((d) => {
    if (d.id !== id) return d;
    hit = true;
    return fn(d);
  });
  return hit ? { ...s, deliverables } : s;
}

export function applyDeliverablePatch(s: RoadmapSnapshot, id: string, changes: DeliverableChanges, people: PersonRef[]): RoadmapSnapshot {
  const epicsById = epicMap(s);
  return mapDeliverable(s, id, (d) => applyDeliverableChanges(d, changes, people, epicsById));
}

/** Restore `keys` to their pre-edit values, but only where nobody changed them since the optimistic write. */
export function rollbackDeliverable(
  s: RoadmapSnapshot,
  id: string,
  keys: Array<keyof Deliverable>,
  before: Deliverable,
  optimistic: Deliverable,
): RoadmapSnapshot {
  return mapDeliverable(s, id, (cur) => restoreKeys(cur, keys, before, optimistic));
}

/** Replace a deliverable (e.g. with the server's canonical copy). Appends it if it is not cached. */
export function upsertDeliverable(s: RoadmapSnapshot, d: Deliverable, replaceId: string = d.id): RoadmapSnapshot {
  const exists = s.deliverables.some((x) => x.id === replaceId);
  const deliverables = exists ? s.deliverables.map((x) => (x.id === replaceId ? d : x)) : [...s.deliverables, d];
  return { ...s, deliverables };
}

export function removeDeliverable(s: RoadmapSnapshot, id: string): RoadmapSnapshot {
  return { ...s, deliverables: s.deliverables.filter((d) => d.id !== id) };
}

let tempCounter = 0;
export const TEMP_PREFIX = 'tmp-';
export const isTempId = (id: string): boolean => id.startsWith(TEMP_PREFIX);
export const newTempId = (): string => `${TEMP_PREFIX}${Date.now().toString(36)}-${++tempCounter}`;

export function makeTempDeliverable(input: CreateDeliverable, id: string, s: RoadmapSnapshot, people: PersonRef[]): Deliverable {
  const epicIds = input.epicId ? [input.epicId] : [];
  const position = s.deliverables.reduce((m, d) => Math.max(m, d.position), 0) + 1;
  return {
    id,
    name: input.name,
    url: '',
    productGoalIds: input.productGoalId ? [input.productGoalId] : [],
    epicIds,
    productArea: deriveProductArea(epicIds, epicMap(s)),
    pmOwners: resolvePeople(input.pmOwnerIds ?? [], people),
    assignees: resolvePeople(input.assigneeIds ?? [], people),
    sprint: input.sprint ?? null,
    scopingStatus: input.scopingStatus ?? null,
    scopingDue: input.scopingDue ?? null,
    status: input.status ?? null,
    type: deriveType(epicIds, epicMap(s)),
    description: null,
    key: null, // monday assigns the key; it appears after the create round trip
    subitemCount: 0,
    position,
    updatedAt: null,
  };
}

// ---- product goals -------------------------------------------------------------------------

export function goalKeysTouched(changes: GoalChanges): Array<keyof ProductGoal> {
  return (['name', 'targetDate', 'status'] as const).filter((k) => k in changes);
}

export function applyGoalChanges(g: ProductGoal, changes: GoalChanges): ProductGoal {
  const next = { ...g };
  if (changes.name !== undefined) next.name = changes.name;
  if (changes.targetDate !== undefined) next.targetDate = changes.targetDate;
  if (changes.status !== undefined) next.status = changes.status;
  return next;
}

export function applyGoalPatch(s: RoadmapSnapshot, id: string, changes: GoalChanges): RoadmapSnapshot {
  return { ...s, productGoals: s.productGoals.map((g) => (g.id === id ? applyGoalChanges(g, changes) : g)) };
}

export function rollbackGoal(s: RoadmapSnapshot, id: string, keys: Array<keyof ProductGoal>, before: ProductGoal, optimistic: ProductGoal): RoadmapSnapshot {
  return { ...s, productGoals: s.productGoals.map((cur) => (cur.id === id ? restoreKeys(cur, keys, before, optimistic) : cur)) };
}

export function upsertGoal(s: RoadmapSnapshot, g: ProductGoal, replaceId: string = g.id): RoadmapSnapshot {
  const exists = s.productGoals.some((x) => x.id === replaceId);
  return { ...s, productGoals: exists ? s.productGoals.map((x) => (x.id === replaceId ? g : x)) : [...s.productGoals, g] };
}

export function removeGoal(s: RoadmapSnapshot, id: string): RoadmapSnapshot {
  return { ...s, productGoals: s.productGoals.filter((g) => g.id !== id) };
}

// ---- epics -------------------------------------------------------------------------------------

export function epicKeysTouched(changes: EpicChanges): Array<keyof Epic> {
  const keys: Array<keyof Epic> = [];
  if ('name' in changes) keys.push('name');
  if ('productArea' in changes) keys.push('productArea');
  if ('type' in changes) keys.push('type');
  if ('pmOwnerIds' in changes) keys.push('pmOwners');
  if ('rdOwnerIds' in changes) keys.push('rdOwners');
  return keys;
}

export function applyEpicChanges(e: Epic, changes: EpicChanges, people: PersonRef[]): Epic {
  const next: Epic = { ...e };
  if (changes.name !== undefined) next.name = changes.name;
  if (changes.productArea !== undefined) next.productArea = changes.productArea;
  if (changes.type !== undefined) next.type = changes.type;
  if (changes.pmOwnerIds !== undefined) next.pmOwners = resolvePeople(changes.pmOwnerIds, people);
  if (changes.rdOwnerIds !== undefined) next.rdOwners = resolvePeople(changes.rdOwnerIds, people);
  return next;
}

/** Deliverables mirror their epic's product area and type; recompute for every deliverable of `epicId`. */
export function recomputeDerivedAreas(s: RoadmapSnapshot, epicId: string): RoadmapSnapshot {
  const epicsById = epicMap(s);
  let changed = false;
  const deliverables = s.deliverables.map((d) => {
    if (!d.epicIds.includes(epicId)) return d;
    const area = deriveProductArea(d.epicIds, epicsById);
    const type = deriveType(d.epicIds, epicsById);
    if (area === d.productArea && type === d.type) return d;
    changed = true;
    return { ...d, productArea: area, type };
  });
  return changed ? { ...s, deliverables } : s;
}

export function applyEpicPatch(s: RoadmapSnapshot, id: string, changes: EpicChanges, people: PersonRef[]): RoadmapSnapshot {
  const next = { ...s, epics: s.epics.map((e) => (e.id === id ? applyEpicChanges(e, changes, people) : e)) };
  return recomputeDerivedAreas(next, id);
}

export function rollbackEpic(s: RoadmapSnapshot, id: string, keys: Array<keyof Epic>, before: Epic, optimistic: Epic): RoadmapSnapshot {
  const next = { ...s, epics: s.epics.map((cur) => (cur.id === id ? restoreKeys(cur, keys, before, optimistic) : cur)) };
  return recomputeDerivedAreas(next, id);
}

export function upsertEpic(s: RoadmapSnapshot, e: Epic, replaceId: string = e.id): RoadmapSnapshot {
  const exists = s.epics.some((x) => x.id === replaceId);
  const next = { ...s, epics: exists ? s.epics.map((x) => (x.id === replaceId ? e : x)) : [...s.epics, e] };
  return recomputeDerivedAreas(next, e.id);
}

export function removeEpic(s: RoadmapSnapshot, id: string): RoadmapSnapshot {
  return { ...s, epics: s.epics.filter((e) => e.id !== id) };
}

// ---- compare-and-set ---------------------------------------------------------------------------

/** Previous values for the compare-and-set `expected` map, keyed by request field name. */
export function expectedForDeliverable(d: Deliverable, changes: DeliverableChanges): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if ('name' in changes) out.name = d.name;
  if ('productGoalId' in changes) out.productGoalId = d.productGoalIds[0] ?? null;
  if ('epicId' in changes) out.epicId = d.epicIds[0] ?? null;
  if ('pmOwnerIds' in changes) out.pmOwnerIds = d.pmOwners.map((p) => p.id);
  if ('assigneeIds' in changes) out.assigneeIds = d.assignees.map((p) => p.id);
  if ('sprint' in changes) out.sprint = d.sprint;
  if ('scopingStatus' in changes) out.scopingStatus = d.scopingStatus;
  if ('scopingDue' in changes) out.scopingDue = d.scopingDue;
  if ('status' in changes) out.status = d.status;
  return out;
}

export function expectedForGoal(g: ProductGoal, changes: GoalChanges): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if ('name' in changes) out.name = g.name;
  if ('targetDate' in changes) out.targetDate = g.targetDate;
  if ('status' in changes) out.status = g.status;
  return out;
}

export function expectedForEpic(e: Epic, changes: EpicChanges): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if ('name' in changes) out.name = e.name;
  if ('productArea' in changes) out.productArea = e.productArea;
  if ('type' in changes) out.type = e.type;
  if ('pmOwnerIds' in changes) out.pmOwnerIds = e.pmOwners.map((p) => p.id);
  if ('rdOwnerIds' in changes) out.rdOwnerIds = e.rdOwners.map((p) => p.id);
  return out;
}
